import { useRef } from 'react';
import { brandingColors } from '@/mocks/registerRadio';
import { ACCEPTED_IMAGE_TYPES, MAX_FILE_SIZE_MB } from '../types';
import type { RadioFormData } from '../types';

interface LogoBrandingFormProps {
  formData: RadioFormData;
  updateField: (field: keyof RadioFormData, value: string) => void;
  setFormData: React.Dispatch<React.SetStateAction<RadioFormData>>;
  errors: Record<string, string>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  uploadProgress: number;
  uploadStatus: 'idle' | 'simulating' | 'done';
  setUploadProgress: React.Dispatch<React.SetStateAction<number>>;
  setUploadStatus: React.Dispatch<React.SetStateAction<'idle' | 'simulating' | 'done'>>;
  isDragging: boolean;
  setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function LogoBrandingForm({
  formData,
  updateField,
  setFormData,
  errors,
  setErrors,
  uploadProgress,
  uploadStatus,
  setUploadProgress,
  setUploadStatus,
  isDragging,
  setIsDragging,
}: LogoBrandingFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLogoFile = (file: File) => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setErrors((prev) => ({ ...prev, logo: 'Formato no soportado. Usa JPG, PNG, WebP o SVG.' }));
      return;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, logo: 'La imagen debe pesar menos de 10 MB.' }));
      return;
    }

    setUploadStatus('simulating');
    setUploadProgress(0);
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 30 + 10;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        setUploadProgress(100);
        setUploadStatus('done');
      }
      setUploadProgress(Math.min(progress, 100));
    }, 120);

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setFormData((prev) => ({ ...prev, logoFile: file, logoPreview: dataUrl }));
      if (errors.logo) {
        setErrors((prev) => { const n = { ...prev }; delete n.logo; return n; });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleLogoFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleLogoFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveLogo = () => {
    setFormData((prev) => ({ ...prev, logoFile: null, logoPreview: '' }));
    setUploadStatus('idle');
    setUploadProgress(0);
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
          Logo de la radio
        </label>

        {!formData.logoPreview ? (
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all group ${
              isDragging
                ? 'border-primary-400 bg-primary-50/60 scale-[1.01]'
                : 'border-background-300 hover:border-primary-300 hover:bg-background-100'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/svg+xml"
              onChange={handleFileInput}
              className="hidden"
            />

            {uploadStatus === 'simulating' ? (
              <div className="space-y-3">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-primary-100 flex items-center justify-center">
                  <i className="ri-upload-cloud-2-line text-2xl text-primary-500 animate-bounce"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-800">Subiendo logo...</p>
                  <p className="text-[11px] text-foreground-400 mt-0.5">{Math.round(uploadProgress)}%</p>
                </div>
                <div className="w-full max-w-[200px] mx-auto h-1.5 rounded-full bg-background-200 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary-500 transition-all duration-200"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-background-200 flex items-center justify-center group-hover:bg-primary-100 group-hover:scale-110 transition-all duration-200">
                  <i className="ri-image-add-line text-2xl text-foreground-400 group-hover:text-primary-500 transition-colors"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-700">
                    Arrastra tu logo aquí o haz clic
                  </p>
                  <p className="text-[11px] text-foreground-400 mt-0.5">
                    JPG, PNG, WebP, SVG — máximo {MAX_FILE_SIZE_MB} MB
                  </p>
                  <p className="text-[10px] text-foreground-300 mt-1">
                    Recomendado: 512×512 px, fondo transparente
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="relative rounded-xl overflow-hidden border border-background-200 bg-background-100">
            <div className="p-6 flex items-center justify-center bg-background-100">
              <img
                src={formData.logoPreview}
                alt="Logo preview"
                className="w-32 h-32 object-contain rounded-2xl bg-background-50"
              />
            </div>
            <div className="flex items-center gap-2 px-4 py-3 bg-background-50 border-t border-background-200">
              <div className="w-7 h-7 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                <i className="ri-check-line text-green-600 text-xs font-bold"></i>
              </div>
              <span className="text-xs font-medium text-foreground-700 truncate flex-1">
                {formData.logoFile?.name || 'Logo cargado'}
              </span>
              <button
                type="button"
                onClick={handleRemoveLogo}
                className="px-3 py-1.5 text-[11px] font-semibold text-red-600 hover:bg-red-50 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
              >
                Quitar
              </button>
            </div>
          </div>
        )}

        {errors.logo && <p className="text-[11px] text-red-500 mt-1">{errors.logo}</p>}
      </div>

      <div>
        <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
          Color principal
        </label>
        <p className="text-[10px] text-foreground-400 mb-3">
          Este color se usará en tu página de radio y en el dial digital. Podés cambiarlo después desde el panel.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          {brandingColors.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => updateField('accentColor', c.value)}
              className="relative group cursor-pointer"
              title={c.label}
            >
              <div
                className={`w-9 h-9 rounded-full transition-all duration-200 ${
                  formData.accentColor === c.value
                    ? 'ring-2 ring-offset-2 ring-foreground-800 scale-110'
                    : 'hover:scale-105'
                }`}
                style={{ backgroundColor: c.value }}
              ></div>
              {formData.accentColor === c.value && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <i className="ri-check-line text-background-50 text-xs font-bold drop-shadow-sm"></i>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}